import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  type Board,
  type BoardConnection,
  type BoardElement,
  type DisplayMode,
  type End,
  NO_COLOUR,
  type OpenBoard,
  type Place,
  changeDepth,
  changeElement,
  contentIsGone,
  clearBoard,
  connect,
  createBoard,
  deleteBoard,
  disconnect,
  duplicateBoard,
  fetchBoard,
  fetchBoards,
  makeBoardDefault,
  placeOnBoard,
  putOnBoard,
  reconnect,
  renameBoard,
  takeOffBoard,
} from '../api/boards'
import { type Failure, asFailure } from '../api/request'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { boardCanBeRearranged, useLayoutStep } from '../frame/layout'
import { useSayWhatIsOpen } from '../frame/open'
import { navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { MARKDOWN } from '../ui/markup'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Failed, Waiting } from '../ui/Status'
import { Boards } from './desk/Boards'
import type { Entry } from './desk/Menu'
import { Canvas, type CanvasHandle } from './desk/Canvas'
import { FindOnBoard } from './desk/FindOnBoard'
import { PutDown } from './desk/PutDown'
import { Properties } from './desk/Properties'
import { ADD_EXISTING, Toolbar } from './desk/Toolbar'
import { useHistory } from './desk/history'
import { boardIn, pathTo, pathToElement, placeIn } from './desk/place'
import { TOP, pathTo as pathInLibrary } from './library/place'

/**
 * The desk: a surface you arrange yourself, and the place signing in leads to.
 *
 * It is **a second view on the same data and not a second place to keep things.** The library stays
 * the backbone — folders, origins, versions, the full-text index — and a board arranges what is
 * already there. Taking a card off a board touches the board and nothing behind it; a document
 * replaced by a newer version shows its new first page at the old place, because a card is a
 * reference and never a copy.
 *
 * Four decisions live in this file rather than anywhere below it:
 *
 * **Which board is open comes out of the address.** `#/desk` is whichever board is marked as the one
 * to land on, `#/desk/<board>` is a particular one, `#/desk/<board>/<element>` jumps to a place on
 * it, `#/desk/boards` is the boards themselves. So every one of those is a link somebody can send,
 * and the browser's own back button walks out of it.
 *
 * **Clearing asks, and everything else does not.** Ordinary changes save themselves and are visible
 * where they happened; clearing deletes and pinning is the entire protection against it, so it is
 * the one control that stops to say how much will go.
 *
 * **Taking a change back lives in the session.** Every change here carries its undoing with it —
 * see `history.ts` — because on a surface a slip weighs more than in a list: a frame let go in the
 * wrong place moves twenty cards at once.
 *
 * **On a phone the board is looked at, not rearranged.** That is `boardCanBeRearranged`, one named
 * function in `frame/layout.ts`, and nothing in this folder mentions a device.
 */
export function Desk() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const place = placeIn(route.path)
  const rearrangeable = boardCanBeRearranged(useLayoutStep())
  const announce = useAnnounce()

  const list = useLoad(useCallback((signal: AbortSignal) => fetchBoards(signal), []))
  const boards = list.load.status === 'ready' ? list.load.value : []
  const openId = boardIn(place, boards)

  const open = useLoad(
    useCallback(
      async (signal: AbortSignal): Promise<OpenBoard | null> =>
        openId === null ? null : fetchBoard(openId, signal),
      [openId],
    ),
  )
  const board = open.load.status === 'ready' ? open.load.value : null

  // What the bar at the top of MyLib says. A board is a thing in the room rather than the room, so
  // once one is open its name is the more useful of the two — and `#/desk/boards` is about the
  // boards themselves, which is what that page is called.
  useSayWhatIsOpen(place.kind === 'boards' ? 'Boards' : (board?.board.name ?? null))

  const [refused, setRefused] = useState<Failure | null>(null)
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set())
  const [selectedLine, setSelectedLine] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [finding, setFinding] = useState(false)
  const [putting, setPutting] = useState(false)
  const [clearing, setClearing] = useState<Board | null>(null)

  const canvas = useRef<CanvasHandle>(null)
  const history = useHistory()

  const elements = useMemo(() => board?.elements ?? [], [board])
  const chosen = useMemo(
    () => elements.filter((element) => selection.has(element.id)),
    [elements, selection],
  )
  const line = board?.connections.find((one) => one.id === selectedLine) ?? null

  /**
   * What is held highlighted: whatever the address points at, and nothing else.
   *
   * Out of the address rather than out of a piece of state, so that a search hit, a link somebody
   * sent and the board's own "find on this board" are one behaviour with one implementation — the
   * last of the three navigates, exactly as the first two do.
   */
  const marked = place.kind === 'board' ? place.element : null

  /** Does something that can be refused, and puts the refusal where it can be read. */
  const attempt = async (work: () => Promise<void>) => {
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    }
  }

  const go = (path: readonly string[]) => {
    navigate('desk', path)
  }

  /**
   * Where something new is put down: the middle of what is on the screen.
   *
   * Not the board's origin, which may be far off the side of the pane after somebody has panned —
   * something put down there would be something put down where nobody is looking.
   */
  const middle = (size = DEFAULT_SIZE): Place => {
    const at = canvas.current?.middle() ?? { x: 0, y: 0 }
    return { x: at.x - size / 2, y: at.y - size / 2, width: size, height: size }
  }

  const put = (what: Parameters<typeof putOnBoard>[1]) => {
    if (openId === null) {
      return
    }
    void attempt(async () => {
      const made = await putOnBoard(openId, what)
      open.retry()
      setSelection(new Set([made.id]))
      history.remember({
        label: 'putting that down',
        undo: async () => {
          await takeOffBoard(openId, made.id)
          open.retry()
        },
      })
      announce('On the board.')
    })
  }

  const moved = (
    places: readonly { readonly id: string; readonly place: Place }[],
    before: readonly { readonly id: string; readonly place: Place }[],
  ) => {
    if (openId === null || places.length === 0) {
      return
    }
    void attempt(async () => {
      try {
        await placeOnBoard(openId, places)
        history.remember({
          label: 'moving',
          undo: async () => {
            await placeOnBoard(openId, before)
            open.retry()
          },
        })
      } finally {
        /*
          Whatever happened, the board is read again — and that matters twice over. A save that was
          refused has to put the truth back on the screen rather than leaving somebody looking at a
          move that did not happen; and the surface holds the dragged places on screen until exactly
          this answer arrives, so a missing one would leave them there for good.
        */
        open.retry()
      }
    })
  }

  const change = (wantedChange: Parameters<typeof changeElement>[2]) => {
    if (openId === null || chosen.length === 0) {
      return
    }
    const was = chosen.map((element) => ({
      id: element.id,
      colour: element.colour ?? NO_COLOUR,
      highlighted: element.highlighted,
      pinned: element.pinned,
      displayMode: (element.displayMode ?? 'preview') as DisplayMode,
    }))
    void attempt(async () => {
      await Promise.all(chosen.map((element) => changeElement(openId, element.id, wantedChange)))
      open.retry()
      history.remember({
        label: 'that change',
        undo: async () => {
          await Promise.all(
            was.map((one) =>
              changeElement(openId, one.id, {
                colour: one.colour,
                highlighted: one.highlighted,
                pinned: one.pinned,
                displayMode: one.displayMode,
              }),
            ),
          )
          open.retry()
        },
      })
    })
  }

  const takeOff = () => {
    if (openId === null) {
      return
    }
    if (line !== null) {
      const was = asDrawn(line)
      void attempt(async () => {
        await disconnect(openId, line.id)
        setSelectedLine(null)
        open.retry()
        history.remember({
          label: 'removing that line',
          undo: async () => {
            // The same line again rather than the same row: an id is the service's to give, and
            // nothing on a board is addressed by the id of a line.
            await connect(openId, was)
            open.retry()
          },
        })
        announce('Line removed.')
      })
      return
    }
    const taken = chosen
    void attempt(async () => {
      await Promise.all(taken.map((element) => takeOffBoard(openId, element.id)))
      setSelection(new Set())
      open.retry()
      history.remember({
        label: 'taking that off',
        undo: async () => {
          // Put back where they lay, as what they were. A card is a reference, so putting one back
          // costs nothing anywhere else; a sticky note comes back with its own words.
          await Promise.all(taken.map((element) => putOnBoard(openId, asPutDown(element))))
          open.retry()
        },
      })
      announce(
        taken.some((one) => one.kind === 'card')
          ? 'Off the board. What it pointed at is untouched.'
          : 'Off the board.',
      )
    })
  }

  /**
   * The keys the board answers, and where they are listened for.
   *
   * On the window rather than on the surface, because nothing on a board holds the focus: a card is
   * not a form field, and asking somebody to click the background before Delete would do anything is
   * the kind of rule nobody discovers.
   *
   * **Anything being typed into is left alone.** A field, a text area or the editor keeps every key
   * it is given — Backspace inside a sticky note deletes a character, not the note.
   *
   * `preventDefault` on undo is the whole of what fixes it: the browser's own answer to that pair is
   * to go back, which took the whole application off the board rather than undoing anything on it.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTyping(event.target) || board === null || !rearrangeable) {
        return
      }
      if ((event.key === 'z' || event.key === 'Z') && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        void history.undo()
        return
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selection.size === 0 && selectedLine === null) {
          return
        }
        event.preventDefault()
        takeOff()
        return
      }
      if (event.key === 'Escape') {
        setConnecting(false)
        setSelection(new Set())
        setSelectedLine(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  })

  /**
   * What the right-click menu offers.
   *
   * **Every entry is also somewhere else** — in the panel beside the board or in one of the two bars
   * — because a menu nobody can open with a finger must never be the only way to reach anything. It
   * is here because on a Mac or a PC people reach for it, and getting the browser's own menu on a
   * surface is the moment an application stops feeling like one.
   */
  const menuFor = (on: BoardElement | null): Entry[] => {
    if (on === null) {
      return [
        {
          id: 'add',
          label: ADD_EXISTING,
          run: () => {
            setPutting(true)
          },
        },
        {
          id: 'sticky',
          label: 'New sticky note',
          run: () => {
            put({ kind: 'sticky', text: 'New note', format: MARKDOWN, place: middle() })
          },
        },
        { id: 'fit', label: 'Fit everything in', run: () => canvas.current?.fitAll() },
      ]
    }
    const several = selection.size > 1
    return [
      ...(on.kind === 'card' && !contentIsGone(on)
        ? [
            {
              id: 'open',
              label: 'Open what this points at',
              run: () => {
                openContent(on)
              },
            },
          ]
        : []),
      // Said in words, because the two other ways into the words are gestures — a double click and a
      // second tap — and nothing in MyLib may be reachable by gesture alone.
      ...((on.kind === 'sticky' || on.kind === 'text') && !on.locked
        ? [
            {
              id: 'write',
              label: on.kind === 'sticky' ? 'Write on this note' : 'Edit this text',
              run: () => {
                canvas.current?.write(on.id)
              },
            },
          ]
        : []),
      {
        id: 'front',
        label: 'Bring to the front',
        run: () => {
          depth(true)
        },
      },
      {
        id: 'back',
        label: 'Send to the back',
        run: () => {
          depth(false)
        },
      },
      {
        id: 'pinned',
        label: on.pinned ? 'No longer permanent' : 'Permanent — survives clearing',
        run: () => {
          change({ pinned: !on.pinned })
        },
      },
      {
        id: 'locked',
        label: on.locked ? 'Unlock' : 'Lock',
        run: () => {
          change({ locked: !on.locked })
        },
      },
      {
        id: 'off',
        label: several ? 'Take them off the board' : 'Take off the board',
        danger: true,
        run: takeOff,
      },
    ]
  }

  /** Brings the selection to the front of the board, or puts it behind everything. */
  const depth = (toFront: boolean) => {
    if (openId === null) {
      return
    }
    void attempt(async () => {
      try {
        await changeDepth(
          openId,
          chosen.map((one) => one.id),
          toFront,
        )
      } finally {
        open.retry()
      }
    })
  }

  const openContent = (element: BoardElement) => {
    if (element.document !== null) {
      const document = element.document
      const where =
        document.folderId === null ? TOP : ({ kind: 'folder', id: document.folderId } as const)
      navigate('library', pathInLibrary(where, document.id))
      return
    }
    if (element.note !== null) {
      navigate('notes', [element.note.id])
      return
    }
    if (element.task !== null) {
      navigate('tasks', [element.task.id])
    }
  }

  const drawn = (from: End, to: End) => {
    if (openId === null) {
      return
    }
    setConnecting(false)
    void attempt(async () => {
      const made = await connect(openId, {
        from,
        to,
        routing: 'straight',
        waypoints: [],
        heads: 'to',
        pinned: false,
      })
      open.retry()
      setSelectedLine(made.id)
      history.remember({
        label: 'that line',
        undo: async () => {
          await disconnect(openId, made.id)
          open.retry()
        },
      })
    })
  }

  const changeLine = (which: BoardConnection, wantedLine: Partial<BoardConnection>) => {
    if (openId === null) {
      return
    }
    // How it stood a moment ago, whole. A line is small enough to put back as it was rather than by
    // undoing one property of it, and putting it back as it was cannot get out of step with what was
    // changed.
    const was = asDrawn(which)
    void attempt(async () => {
      try {
        await reconnect(openId, which.id, { ...was, ...wantedLine })
        history.remember({
          label: 'that line',
          undo: async () => {
            await reconnect(openId, which.id, was)
            open.retry()
          },
        })
      } finally {
        // Always, refused or not — the surface holds a bent line on screen until this answer comes.
        open.retry()
      }
    })
  }

  if (place.kind === 'boards') {
    return (
      <section className="canvas canvas--boards" aria-label="Boards">
        {refused !== null && <Failed failure={refused} />}
        <Boards
          boards={boards}
          open={openId}
          countOn={(one) => (one.id === openId ? elements.length : null)}
          onOpen={(one) => {
            go(pathTo({ kind: 'board', id: one.id, element: null }))
          }}
          onCreate={(name) => {
            void attempt(async () => {
              const made = await createBoard(name)
              list.retry()
              go(pathTo({ kind: 'board', id: made.id, element: null }))
              announce('New board.')
            })
          }}
          onRename={(one, name) => {
            void attempt(async () => {
              await renameBoard(one.id, name)
              list.retry()
            })
          }}
          onMakeDefault={(one) => {
            void attempt(async () => {
              await makeBoardDefault(one.id)
              list.retry()
              announce('You will land here.')
            })
          }}
          onDuplicate={(one, name) => {
            void attempt(async () => {
              const copy = await duplicateBoard(one.id, name)
              list.retry()
              announce('Copied.')
              go(pathTo({ kind: 'board', id: copy.id, element: null }))
            })
          }}
          onDelete={(one) => {
            void attempt(async () => {
              await deleteBoard(one.id)
              list.retry()
              if (one.id === openId) {
                go([])
              }
              announce('Board deleted.')
            })
          }}
        />
      </section>
    )
  }

  return (
    <section className="canvas canvas--board" aria-label={board?.board.name ?? 'Desk'}>
      <div className="board__area">
        <header className="board__bar">
          <h2 className="board__name">{board?.board.name ?? 'Desk'}</h2>
          <Button
            tone="floating"
            onClick={() => {
              go(['boards'])
            }}
          >
            Boards
          </Button>
          {/*
          Clearing acts on the board somebody is *looking at*, so it stands on the board and not in a
          list of them: from the list it is a decision about something out of sight, and this is the
          one control in the area that deletes.
        */}
          {board !== null && rearrangeable && (
            <Button
              tone="floating"
              className="control--danger"
              onClick={() => {
                setClearing(board.board)
              }}
            >
              Clear the board
            </Button>
          )}
        </header>

        {clearing !== null && (
          <Clearing
            board={clearing}
            count={elements.filter((one) => !one.pinned).length}
            onGiveUp={() => {
              setClearing(null)
            }}
            onClear={() => {
              const which = clearing
              setClearing(null)
              void attempt(async () => {
                const removed = await clearBoard(which.id)
                open.retry()
                setSelection(new Set())
                announce(removed === 1 ? 'One thing went.' : `${removed} things went.`)
              })
            }}
          />
        )}

        {refused !== null && <Failed failure={refused} />}

        {(list.load.status === 'waiting' || open.load.status === 'waiting') && (
          <Waiting label="Fetching your desk." />
        )}
        {list.load.status === 'failed' && (
          <Failed failure={list.load.failure} onRetry={list.retry} title="The desk did not load" />
        )}
        {open.load.status === 'failed' && (
          <Failed
            failure={open.load.failure}
            onRetry={open.retry}
            title="That board did not load"
          />
        )}

        {open.load.status === 'ready' && board === null && (
          <Offer
            title="That board is gone"
            lead="The address points at a board this space no longer has — it may have been deleted on another device."
            action={
              <Button
                tone="primary"
                onClick={() => {
                  go([])
                }}
              >
                Go to your desk
              </Button>
            }
          />
        )}

        {board !== null && elements.length === 0 && !putting && (
          <Offer
            title="Your desk"
            lead="Lay things out the way you would on a table: the script, the slides from the two lectures it came up in, a note with the cases you have not understood. A folder tells you where something is; this tells you what belongs with what."
            points={[
              'Cards on your documents, notes and open points — showing their content, not their name',
              'Sticky notes, text blocks and frames that live only here',
              'Lines between two things, and a copy of the whole board when a semester ends',
            ]}
            action={
              rearrangeable ? (
                <Button
                  tone="primary"
                  onClick={() => {
                    setPutting(true)
                  }}
                >
                  {ADD_EXISTING}
                </Button>
              ) : undefined
            }
            note={
              rearrangeable
                ? undefined
                : 'Arranging things is done on a Mac or an iPad. Here you can look and move about.'
            }
          />
        )}

        {board !== null && (
          <Canvas
            handle={canvas}
            board={board}
            rearrangeable={rearrangeable}
            selection={selection}
            onSelection={setSelection}
            selectedLine={selectedLine}
            onSelectLine={setSelectedLine}
            marked={marked}
            connecting={connecting}
            onConnected={drawn}
            onMoved={moved}
            onOpen={openContent}
            onWrite={async (element, text) => {
              if (openId === null) {
                return
              }
              await changeElement(openId, element.id, { text })
              open.retry()
            }}
            onMarkup={async (element, format) => {
              if (openId === null) {
                return
              }
              // A label and nothing else: no byte of the text moves, so there is nothing to save
              // beside it and nothing to warn about.
              await changeElement(openId, element.id, { format })
              open.retry()
            }}
            onDisplayPage={(element, page) => {
              if (openId === null || page < 1 || page === element.displayPage) {
                return
              }
              void attempt(async () => {
                await changeElement(openId, element.id, { displayPage: page })
                open.retry()
              })
            }}
            /*
              Reading or writing, chosen on the card itself. Kept exactly as a page turn is: written
              down so the card is found as it was left, and out of the undo stack, because deciding
              to read something is not a change to the arrangement.
            */
            onDisplayMode={(element, mode) => {
              if (openId === null || mode === (element.displayMode ?? 'preview')) {
                return
              }
              void attempt(async () => {
                await changeElement(openId, element.id, { displayMode: mode })
                open.retry()
              })
            }}
            onLineChanged={(which, waypoints) => {
              changeLine(which, { waypoints })
            }}
            onEndMoved={(which, end, to) => {
              changeLine(which, end === 'from' ? { from: to } : { to })
            }}
            onMenu={menuFor}
          />
        )}

        {board !== null && (
          <Toolbar
            rearrangeable={rearrangeable}
            connecting={connecting}
            canUndo={history.last !== null}
            onPutDown={() => {
              setPutting(true)
            }}
            onSticky={() => {
              put({ kind: 'sticky', text: 'New note', format: MARKDOWN, place: middle() })
            }}
            onTextBlock={() => {
              put({ kind: 'text', text: '## Heading', format: MARKDOWN, place: middle() })
            }}
            onFrame={() => {
              put({ kind: 'frame', label: 'Frame', place: middle(FRAME_SIZE) })
            }}
            onConnect={() => {
              setConnecting(!connecting)
            }}
            onZoom={(factor) => {
              canvas.current?.zoomBy(factor)
            }}
            onFitAll={() => {
              canvas.current?.fitAll()
            }}
            onFind={() => {
              setFinding(!finding)
            }}
            onUndo={() => {
              void history.undo()
            }}
          />
        )}

        {connecting && (
          /*
          What "connect" is waiting for, said out loud. A mode nobody can see is a mode that feels
          like the board has stopped answering — and this one changes what a tap on a card does, so
          it owes an explanation while it lasts.
        */
          <p className="board__hint" role="status">
            Drag from one thing to another. Either end may start or finish on the bare surface — let
            go over an anchor point to fasten it exactly there.
          </p>
        )}

        {finding && board !== null && (
          <FindOnBoard
            elements={elements}
            onGo={(element) => {
              // The same movement a deep link makes, because it *is* one: the address is written and
              // the board follows it. One behaviour, one implementation.
              setSelection(new Set([element.id]))
              go(pathToElement(board.board.id, element.id))
              canvas.current?.jumpTo(element.id)
            }}
            onClose={() => {
              setFinding(false)
            }}
          />
        )}

        {putting && (
          <PutDown
            onClose={() => {
              setPutting(false)
            }}
            onChoose={(what) => {
              setPutting(false)
              put({
                kind: 'card',
                contentKind: what.kind,
                contentId: what.id,
                label: what.label,
                place: middle(),
              })
            }}
          />
        )}
      </div>

      {rearrangeable && board !== null && (
        <Properties
          selection={chosen}
          line={line}
          onColour={(colour) => {
            change({ colour: colour ?? NO_COLOUR })
          }}
          onHighlight={(highlighted) => {
            change({ highlighted })
          }}
          onPin={(pinned) => {
            change({ pinned })
          }}
          onLock={(locked) => {
            change({ locked })
          }}
          onFit={(fit) => {
            change({ fit })
          }}
          onDisplayMode={(displayMode) => {
            change({ displayMode })
          }}
          onClose={() => {
            setSelection(new Set())
            setSelectedLine(null)
          }}
          onDepth={depth}
          onTakeOff={takeOff}
          onOpen={openContent}
          onRename={(element, name) => {
            if (openId === null) {
              return
            }
            void attempt(async () => {
              try {
                await changeElement(openId, element.id, { label: name })
              } finally {
                open.retry()
              }
            })
          }}
          onRouting={(routing) => {
            if (line !== null) {
              changeLine(line, { routing })
            }
          }}
          onHeads={(heads) => {
            if (line !== null) {
              changeLine(line, { heads })
            }
          }}
          onStraighten={() => {
            if (line !== null) {
              changeLine(line, { waypoints: [] })
            }
          }}
          onPinLine={(pinned) => {
            if (line !== null) {
              changeLine(line, { pinned })
            }
          }}
        />
      )}
    </section>
  )
}

/**
 * The one question this area asks, and why it asks it.
 *
 * Clearing a board **deletes**: cards vanish without consequence, but sticky notes, text blocks and
 * frames existed only there and are gone for good. Pinning is the whole of the protection, and it
 * has to have been done beforehand — so this says how much would go, which is what turns the control
 * into a decision rather than a surprise.
 */
function Clearing({
  board,
  count,
  onClear,
  onGiveUp,
}: {
  readonly board: Board
  /** How much would go, or `null` when this is not the board that is open and it cannot be counted. */
  readonly count: number | null
  readonly onClear: () => void
  readonly onGiveUp: () => void
}) {
  return (
    <div className="notice notice--ask" role="alertdialog" aria-label={`Clear ${board.name}`}>
      <p className="notice__text">
        {count === null
          ? `Everything on ${board.name} that is not fixed in place goes, for good.`
          : count === 1
            ? `One thing on ${board.name} is not fixed in place, and it goes for good.`
            : `${count} things on ${board.name} are not fixed in place, and they go for good.`}
      </p>
      <p className="notice__detail">
        Cards vanish without consequence — the documents behind them stay in the library. Sticky
        notes, text blocks and frames exist only here and cannot be brought back.
      </p>
      <div className="controls controls--tight">
        <Button tone="quiet" className="control--danger" onClick={onClear}>
          Clear it
        </Button>
        <Button tone="bare" onClick={onGiveUp}>
          Leave it
        </Button>
      </div>
    </div>
  )
}

/** A line as it stands, in the shape the service takes it back in. */
function asDrawn(line: BoardConnection) {
  return {
    from: line.from,
    to: line.to,
    routing: line.routing,
    waypoints: line.waypoints,
    heads: line.heads,
    colour: line.colour,
    pinned: line.pinned,
  }
}

/** Something on a board as it stands, in the shape it would be laid down in again. */
function asPutDown(element: BoardElement) {
  return {
    kind: element.kind,
    ...(element.contentKind === null ? {} : { contentKind: element.contentKind }),
    ...(element.contentId === null ? {} : { contentId: element.contentId }),
    ...(element.label === null ? {} : { label: element.label }),
    ...(element.text === null ? {} : { text: element.text }),
    ...(element.format === null ? {} : { format: element.format }),
    ...(element.colour === null ? {} : { colour: element.colour }),
    place: {
      x: element.x,
      y: element.y,
      width: element.width,
      height: element.height,
      z: element.z,
    },
  }
}

/**
 * Whether the key that was just pressed belongs to something being typed into.
 *
 * A field, a text area, anything marked editable — and CodeMirror, which is the third of those. Every
 * key such a thing is given is its own: Backspace inside a sticky note takes out a character and not
 * the note.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  )
}

/** How big something new is when nobody has said otherwise. A square: a card is a picture first. */
const DEFAULT_SIZE = 240

/** A frame starts big enough to put three or four things into without resizing it first. */
const FRAME_SIZE = 640
